import { useRef, useState } from 'react';

import { Modal, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useGetOwnerSupplierBusinessNumberExists, usePostOwnerSupplier } from '~/hooks/vender';
import { useLoadingStore } from '~/store';
import { focusFirstErrorField, formatDate, validateRepresentativeTelFormat, validateTelFormat } from '~/utils';

import {
  DEFAULT_INPUT_STATUS,
  ERROR_BUSINESS_NUMBER_SEARCH_STATUS,
  ERROR_DUPLICATED_BUSINESS_NUMBER_STATUS,
  ERROR_INVALID_FORMAT_INPUT_STATUS,
  ERROR_REQUIRED_INPUT_STATUS,
  PICKUP_SHIPPING_TYPES,
  TOAST_MESSAGE,
  VENDER_DATE_FORMAT,
} from '~/constants';

import VenderSupplierAddModalDefaultInfoSection from '../VenderSupplierAddModalDefaultInfoSection/VenderSupplierAddModalDefaultInfoSection';
import VenderSupplierAddModalManagerInfoSection from '../VenderSupplierAddModalManagerInfoSection/VenderSupplierAddModalManagerInfoSection';
import { VenderSupplierAddModalShippingAddressInfoSection } from '../VenderSupplierAddModalShippingAddressInfoSection';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypePickupInfo,
  TypePostOwnerSupplierParams,
  TypeSupplierAddFormStatus,
  TypeSupplierAddFormValue,
  TypeVenderSupplierAddErrorFieldRefs,
} from '~/types/vender.types';

import styles from './VenderSupplierAddModal.module.scss';

type TypeVenderSupplierAddModalProps = {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
};

export const DEFAULT_SUPPLIER_ADD_FORM_VALUE: TypeSupplierAddFormValue = {
  ownerSupplierCode: '',
  businessNumber: '',
  supplierName: '',
  businessConditions: '',
  businessCategory: '',
  ceoName: '',
  zipCode: '',
  address: '',
  detailAddress: '',
  tel: '',
  fax: '',
  dealStartDate: null,
  dealEndDate: null,
  isUseStatus: true,
  isUsePickup: false,
  isUseKurlyPickup: false,
  isUseMilkRunPickup: false,
  kurlyPickupInfo: {
    zipCode: '',
    address: '',
    detailAddress: '',
    contactNumber: '',
  },
  milkRunPickupInfo: {
    zipCode: '',
    address: '',
    detailAddress: '',
    contactNumber: '',
  },
  managerInfoList: [],
};

export const DEFAULT_SUPPLIER_ADD_FORM_STATUS = {
  businessNumber: DEFAULT_INPUT_STATUS,
  supplierName: DEFAULT_INPUT_STATUS,
  tel: DEFAULT_INPUT_STATUS,
  fax: DEFAULT_INPUT_STATUS,
  pickupType: DEFAULT_INPUT_STATUS,
  kurlyPickupZipCode: DEFAULT_INPUT_STATUS,
  kurlyPickupAddress: DEFAULT_INPUT_STATUS,
  kurlyPickupContactNumber: DEFAULT_INPUT_STATUS,
  milkRunPickupZipCode: DEFAULT_INPUT_STATUS,
  milkRunPickupAddress: DEFAULT_INPUT_STATUS,
  milkRunPickupContactNumber: DEFAULT_INPUT_STATUS,
};

const DEFAULT_SUPPLIER_ADD_ERROR_FIELD_REFS: TypeVenderSupplierAddErrorFieldRefs = {
  businessNumber: null,
  supplierName: null,
  tel: null,
  fax: null,
  pickupType: null,
  kurlyPickupZipCode: null,
  kurlyPickupAddress: null,
  kurlyPickupContactNumber: null,
  milkRunPickupZipCode: null,
  milkRunPickupAddress: null,
  milkRunPickupContactNumber: null,
};

/**
 * [화주사정보관리 > 공급사관리] 공급사 등록 모달 컴포넌트
 */
const VenderSupplierAddModal = ({ open, onCancel, onConfirm }: TypeVenderSupplierAddModalProps) => {
  const { getOwnerSupplierBusinessNumberExists } = useGetOwnerSupplierBusinessNumberExists();
  const { postOwnerSupplier } = usePostOwnerSupplier();

  const { setIsLoading } = useLoadingStore();

  const [venderSupplierAddForm, setVenderSupplierAddForm] = useState<TypeSupplierAddFormValue>(
    DEFAULT_SUPPLIER_ADD_FORM_VALUE
  );

  const [venderSupplierAddFormStatus, setVenderSupplierAddFormStatus] = useState<TypeSupplierAddFormStatus>(
    DEFAULT_SUPPLIER_ADD_FORM_STATUS
  );

  /** 사업자번호 유효성 or 중복 체크 여부 (boolean:체크 후 결과, null:체크 전) */
  const [isBusinessNumberValid, setIsBusinessNumberValid] = useState<boolean | null>(null);
  const [isBusinessNumberExist, setIsBusinessNumberExist] = useState<boolean | null>(null);

  const errorFieldRefs = useRef<TypeVenderSupplierAddErrorFieldRefs>(DEFAULT_SUPPLIER_ADD_ERROR_FIELD_REFS);

  const fetchBusinessNumberExist = async () => {
    try {
      setIsLoading(true);

      const isExist = await getOwnerSupplierBusinessNumberExists({
        businessNumber: venderSupplierAddForm.businessNumber,
      });

      setIsBusinessNumberExist(isExist);

      return isExist;
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createPickupShippingAddresses = (
    usePickup: boolean,
    useKurlyPickup: boolean,
    useMilkRunPickup: boolean,
    kurlyPickupInfo: TypePickupInfo,
    milkRunPickupInfo: TypePickupInfo
  ) => {
    if (!usePickup) return [];

    const addresses = [];

    if (useKurlyPickup) {
      addresses.push({
        shippingType: PICKUP_SHIPPING_TYPES.KURLY_PICKUP,
        zipCode: kurlyPickupInfo.zipCode,
        address: kurlyPickupInfo.address,
        detailAddress: kurlyPickupInfo.detailAddress,
        contactNumber: kurlyPickupInfo.contactNumber,
      });
    }
    if (useMilkRunPickup) {
      addresses.push({
        shippingType: PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP,
        zipCode: milkRunPickupInfo.zipCode,
        address: milkRunPickupInfo.address,
        detailAddress: milkRunPickupInfo.detailAddress,
        contactNumber: milkRunPickupInfo.contactNumber,
      });
    }

    return addresses;
  };

  const fetchSupplierAdd = async () => {
    try {
      setIsLoading(true);

      const {
        ownerSupplierCode,
        businessNumber,
        supplierName,
        businessConditions,
        businessCategory,
        ceoName,
        zipCode,
        address,
        detailAddress,
        tel,
        fax,
        dealStartDate,
        dealEndDate,
        isUseStatus,
        isUsePickup,
        isUseKurlyPickup,
        isUseMilkRunPickup,
        kurlyPickupInfo,
        milkRunPickupInfo,
        managerInfoList,
      } = venderSupplierAddForm;

      const params: TypePostOwnerSupplierParams = {
        ownerOriginSupplierCode: ownerSupplierCode.trim(),
        businessNumber,
        businessNumberChecked: isBusinessNumberValid === true,
        ownerSupplierName: supplierName.trim(),
        businessConditions: businessConditions.trim(),
        businessCategory: businessCategory.trim(),
        ceoName: ceoName.trim(),
        zipCode,
        address,
        detailAddress: detailAddress.trim(),
        ownerSupplierTel: tel,
        ownerSupplierFax: fax,
        dealStartDate: formatDate(dealStartDate, VENDER_DATE_FORMAT.DATE_TIME),
        dealEndDate: formatDate(dealEndDate, VENDER_DATE_FORMAT.DATE_TIME),
        useStatus: isUseStatus ? 'Y' : 'N',
        ownerSupplierShippingAddressRequests: createPickupShippingAddresses(
          isUsePickup,
          isUseKurlyPickup,
          isUseMilkRunPickup,
          kurlyPickupInfo,
          milkRunPickupInfo
        ),
        ownerSupplierManagerCreateRequests: managerInfoList,
      };

      await postOwnerSupplier(params);

      toastService.addToast({
        message: '성공적으로 저장되었습니다.',
      });

      await onConfirm();
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateBusinessNumber = async (
    formValue: TypeSupplierAddFormValue,
    formStatus: TypeSupplierAddFormStatus
  ): Promise<boolean> => {
    if (formValue.businessNumber.length === 0) {
      formStatus.businessNumber = ERROR_REQUIRED_INPUT_STATUS;
    } else if (isBusinessNumberValid === null) {
      formStatus.businessNumber = ERROR_BUSINESS_NUMBER_SEARCH_STATUS;
    } else if (isBusinessNumberExist === null) {
      const isExist = await fetchBusinessNumberExist();

      if (isExist) {
        formStatus.businessNumber = ERROR_DUPLICATED_BUSINESS_NUMBER_STATUS;
      }
    } else if (isBusinessNumberExist === true) {
      formStatus.businessNumber = ERROR_DUPLICATED_BUSINESS_NUMBER_STATUS;
    }

    const isValid = formStatus.businessNumber.status !== 'error';

    return isValid;
  };

  const validateBasicInfo = (formValue: TypeSupplierAddFormValue, formStatus: TypeSupplierAddFormStatus): boolean => {
    if (formValue.supplierName.trim() === '') {
      formStatus.supplierName = ERROR_REQUIRED_INPUT_STATUS;
    }

    if (formValue.tel.length > 0 && !validateRepresentativeTelFormat(formValue.tel)) {
      formStatus.tel = ERROR_INVALID_FORMAT_INPUT_STATUS;
    }

    if (formValue.fax.length > 0 && !validateTelFormat(formValue.fax)) {
      formStatus.fax = ERROR_INVALID_FORMAT_INPUT_STATUS;
    }

    const isValid = !(
      formStatus.supplierName.status === 'error' ||
      formStatus.tel.status === 'error' ||
      formStatus.fax.status === 'error'
    );

    return isValid;
  };

  const validatePickupInfo = (formValue: TypeSupplierAddFormValue, formStatus: TypeSupplierAddFormStatus): boolean => {
    let isValid = true;

    if (formValue.isUsePickup && !formValue.isUseKurlyPickup && !formValue.isUseMilkRunPickup) {
      formStatus.pickupType = ERROR_REQUIRED_INPUT_STATUS;
      isValid = false;
    }

    if (formValue.isUsePickup && formValue.isUseKurlyPickup) {
      const { zipCode, address, contactNumber } = formValue.kurlyPickupInfo;

      if (zipCode.length === 0) {
        formStatus.kurlyPickupZipCode = ERROR_REQUIRED_INPUT_STATUS;
      }

      if (address.length === 0) {
        formStatus.kurlyPickupAddress = ERROR_REQUIRED_INPUT_STATUS;
      }

      if (contactNumber.length === 0) {
        formStatus.kurlyPickupContactNumber = ERROR_REQUIRED_INPUT_STATUS;
      } else if (!validateTelFormat(contactNumber)) {
        formStatus.kurlyPickupContactNumber = ERROR_INVALID_FORMAT_INPUT_STATUS;
      }

      isValid =
        isValid &&
        !(
          formStatus.kurlyPickupZipCode.status === 'error' ||
          formStatus.kurlyPickupAddress.status === 'error' ||
          formStatus.kurlyPickupContactNumber.status === 'error'
        );
    }

    if (formValue.isUsePickup && formValue.isUseMilkRunPickup) {
      const { zipCode, address, contactNumber } = formValue.milkRunPickupInfo;
      formStatus.milkRunPickupZipCode =
        zipCode.length === 0 ? ERROR_REQUIRED_INPUT_STATUS : formStatus.milkRunPickupZipCode;
      formStatus.milkRunPickupAddress =
        address.length === 0 ? ERROR_REQUIRED_INPUT_STATUS : formStatus.milkRunPickupAddress;
      formStatus.milkRunPickupContactNumber =
        contactNumber.length === 0 ? ERROR_REQUIRED_INPUT_STATUS : formStatus.milkRunPickupContactNumber;
      formStatus.milkRunPickupContactNumber =
        contactNumber.length > 0 && !validateTelFormat(contactNumber)
          ? ERROR_INVALID_FORMAT_INPUT_STATUS
          : formStatus.milkRunPickupContactNumber;

      isValid =
        isValid &&
        !(
          formStatus.milkRunPickupZipCode.status === 'error' ||
          formStatus.milkRunPickupAddress.status === 'error' ||
          formStatus.milkRunPickupContactNumber.status === 'error'
        );
    }

    return isValid;
  };

  const validateForm = async () => {
    const updatedFormStatus = { ...venderSupplierAddFormStatus };

    // 사업자번호 검증
    const isBusinessNumberValid = await validateBusinessNumber(venderSupplierAddForm, updatedFormStatus);
    // 기본 정보 검증
    const isBasicInfoValid = validateBasicInfo(venderSupplierAddForm, updatedFormStatus);
    // 출고지 정보 검증
    const isPickupInfoValid = validatePickupInfo(venderSupplierAddForm, updatedFormStatus);

    setVenderSupplierAddFormStatus(updatedFormStatus);

    return {
      isValid: isBusinessNumberValid && isBasicInfoValid && isPickupInfoValid,
      updatedFormStatus,
    };
  };

  const handleConfirm = async () => {
    const { isValid: isValidForm, updatedFormStatus } = await validateForm();

    if (isValidForm) {
      if (venderSupplierAddForm.managerInfoList.length < 1) {
        dialogService.addDialog({
          children: '담당자 정보를 1개 이상 추가 후 등록해주세요.',
        });

        return;
      }

      await fetchSupplierAdd();
    } else {
      focusFirstErrorField(errorFieldRefs.current, updatedFormStatus);
    }
  };

  return (
    <Modal
      title="공급사 등록"
      open={open}
      size="xlarge"
      confirmText="등록"
      confirmButtonType="submit"
      onCancel={onCancel}
      onConfirm={handleConfirm}
    >
      <div className={styles['modal-contents-container']}>
        <VenderSupplierAddModalDefaultInfoSection
          errorFieldRefs={errorFieldRefs}
          venderSupplierAddForm={venderSupplierAddForm}
          venderSupplierAddFormStatus={venderSupplierAddFormStatus}
          isBusinessNumberValid={isBusinessNumberValid}
          setVenderSupplierAddForm={setVenderSupplierAddForm}
          setVenderSupplierAddFormStatus={setVenderSupplierAddFormStatus}
          setIsBusinessNumberValid={setIsBusinessNumberValid}
          setIsBusinessNumberExist={setIsBusinessNumberExist}
        />
        <VenderSupplierAddModalShippingAddressInfoSection
          errorFieldRefs={errorFieldRefs}
          venderSupplierAddForm={venderSupplierAddForm}
          venderSupplierAddFormStatus={venderSupplierAddFormStatus}
          setVenderSupplierAddForm={setVenderSupplierAddForm}
          setVenderSupplierAddFormStatus={setVenderSupplierAddFormStatus}
        />
        <VenderSupplierAddModalManagerInfoSection
          venderSupplierAddForm={venderSupplierAddForm}
          setVenderSupplierAddForm={setVenderSupplierAddForm}
        />
      </div>
    </Modal>
  );
};

export default VenderSupplierAddModal;
